import WorkspaceService from 'src/api/workspaces';
import DocumentService from '../../api/documents';
import { fieldTypes } from '../../models/filters';
import Vue from 'vue';

const state = {
  userWorkspaces: [],
  userWorkspacesLoading: true,
  activeWorkspace: null,
  error: null,
  trackedEntitiesData: {
    publication: [],
    patent: [],
    author: [],
    inventor: [],
    assignee: [],
  },
  trackedEntitiesDataLoader: false,
  linkedScansData: [],
  linkedScansDataLoader: false,
  alerts: [],
  alertsDataLoader: false,
  alertsPagination: {
    page: 1,
    rowsPerPage: 10,
    rowsNumber: {
      total: 0,
      unread: 0,
    },
  },
  unreadAlertsOnly: true,
};

const getters = {
  userWorkspaces: (state) =>
    state.userWorkspaces.sort((a, b) => a.sort_index - b.sort_index),
  userWorkspacesLoading: (state) => state.userWorkspacesLoading,
  activeWorkspace: (state) => state.activeWorkspace,
  trackedEntitiesData: (state) => state.trackedEntitiesData,
  trackedEntitiesDataLoader: (state) => state.trackedEntitiesDataLoader,
  linkedScansData: (state, getters, rootState, rootGetters) => {
    const userScans = rootGetters['scans/getUserProfile'];
    if (userScans && state.linkedScansData) {
      return state.linkedScansData.reduce((linkedScans, data) => {
        // Find the scan in the user profile
        const scan = userScans.find(
          (userScan) => userScan.scan_id === data.scan_id
        );
        if (scan) {
          // Add the date the scan was added to the workspace for sorting
          linkedScans.push({
            ...scan,
            added_date: data.created_date,
          });
        }
        return linkedScans;
      }, []);
    } else {
      return [];
    }
  },
  linkedScansDataLoader: (state) => state.linkedScansDataLoader,
  alerts: (state) => state.alerts.filter((alert) => alert.alerts_on),
  unreadAlerts: (state, getters) => {
    if (getters.alerts) {
      return getters.alerts.filter((alert) => !alert.is_read);
    } else {
      return [];
    }
  },
  alertsDataLoader: (state) => state.alertsDataLoader,
  alertsPagination: (state) => state.alertsPagination,
  unreadAlertsOnly: (state) => state.unreadAlertsOnly,
  error: (state) => state.error,
};

const actions = {
  async getWorkspaces({ dispatch }, workspace_id) {
    dispatch('getUserWorkspaces', workspace_id);
  },
  async getUserWorkspaces({ commit, state }, workspace_id) {
    commit('setUserWorkspacesLoading', true);
    const workspaceService = new WorkspaceService();
    const user_id = sessionStorage.getItem('uid');
    const response = await workspaceService.getUserWorkspaces(user_id);

    if (response && !response.error) {
      commit('setUserWorkspaces', response.data);

      if (workspace_id) {
        // Workspace ID was passed in, set that workspace as active
        // Find workspace in userWorkspaces that matches this workspace_id
        const _workspace = state.userWorkspaces.find((workspace) => {
          return workspace.workspace_id === workspace_id;
        });
        // Set workspace as active in state
        commit('setActiveWorkspace', _workspace);
      }
      commit('setUserWorkspacesLoading', false);
      commit('setError', null);
      return true;
    } else {
      commit('setUserWorkspacesLoading', false);
      commit('setError', 'Something went wrong while getting your workspaces');
      return false;
    }
  },
  async createWorkspace({ commit, state }, payload) {
    // Copy the relevant workspaces array in the store
    const workspaces = payload.org_workspace ? [] : [...state.userWorkspaces];

    // Build the payload
    const _payload = {
      ...payload,
      sort_index: workspaces.length,
      user_id: sessionStorage.getItem('uid'),
      org_id: sessionStorage.getItem('organization_id'),
    };

    const workspaceService = new WorkspaceService();
    const response = await workspaceService.createWorkspace(_payload);
    if (response && !response.error) {
      // Build the created workspace object with the workspace_id returned from the service
      const createdWorkspace = {
        ..._payload,
        workspace_id: response.data.workspace_id,
        tracked_entities: response.data.tracked_entities,
      };

      // Add the created workspace to the relevant workspaces array in the store
      if (payload.org_workspace) {
      } else {
        commit('setUserWorkspaces', workspaces.concat(createdWorkspace));
      }
      commit('setError', null);
      // Return the created workspace as it's needed to update routing params
      return createdWorkspace;
    } else {
      commit('setError', 'Something went wrong while creating the workspace');
      return false;
    }
  },
  async updateWorkspace({ commit, state }, payload) {
    // TODO: Create a mutation to update the workspace in the store and update directly instead of doing all this business
    const workspaceService = new WorkspaceService();
    const response = await workspaceService.updateWorkspace(payload);

    if (response && !response.error) {
      // Copy the relevant workspaces array in the store
      const workspaces = payload.org_workspace ? [] : [...state.userWorkspaces];
      // Find the index of the updated workspace
      const workspaceIndex = workspaces.findIndex(
        (w) => w.workspace_id === payload.workspace_id
      );
      // Build the updated workspace object with the properties in the store and the updated properties from the payload
      const updatedWorkspace = {
        ...workspaces[workspaceIndex],
        ...payload,
      };
      // Update the workspace in the workspaces array
      workspaces[workspaceIndex] = updatedWorkspace;

      // Update the relevant workspaces array in the store
      if (payload.org_workspace) {
      } else {
        commit('setUserWorkspaces', workspaces);
      }
      // If the updated workspace is the active workspace, update the active workspace in state
      if (
        state.activeWorkspace &&
        state.activeWorkspace.workspace_id === payload.workspace_id
      ) {
        commit('setActiveWorkspace', updatedWorkspace);
      }
      commit('setError', null);
      return updatedWorkspace;
    } else {
      commit('setError', 'Something went wrong while updating the workspace');
      return false;
    }
  },
  async updateWorkspacesSortOrder({ commit, getters }, payload) {
    const { org_workspace, workspaces: payloadWorkspaces } = payload;
    // The payload workspaces array is sorted in the new order, update each workspace's sort_index property to match its index in the array
    const allUpdatedWorkspaces = payloadWorkspaces.map((p, i) => ({
      ...p,
      sort_index: i,
    }));
    // Get relevant workspaces in the store via the getter because those in state are not sorted by sort_index
    const storeWorkspaces = org_workspace ? [] : [...getters.userWorkspaces];
    // Find the index of the first workspace with a changed sort_index
    const firstChangedWorkspaceIndex = storeWorkspaces.findIndex(
      (w, i) => w.sort_index !== payloadWorkspaces[i].sort_index
    );
    // Get just the workspaces that have a changed sort_index and build the payload for the request, an array of objects with workspace_id and sort_index properties
    const updatedWorkspacesWithNewSortOrder = allUpdatedWorkspaces
      .slice(firstChangedWorkspaceIndex)
      .map((w) => ({
        workspace_id: w.workspace_id,
        sort_index: w.sort_index,
      }));

    // Update the relevant workspaces array in the store ahead of the request so the UI is responsive
    if (org_workspace) {
    } else {
      commit('setUserWorkspaces', allUpdatedWorkspaces);
    }

    // Make request to update sort indexes in the database
    const workspaceService = new WorkspaceService();
    const response = await workspaceService.updateWorkspacesSortOrder(
      updatedWorkspacesWithNewSortOrder
    );
    if (response && !response.error) {
      commit('setError', null);
      return true;
    } else {
      commit(
        'setError',
        'Something went wrong while updating the workspaces order'
      );
      return false;
    }
  },
  async deleteWorkspace({ commit, state }, payload) {
    const { workspace_id, org_workspace } = payload;
    const workspaceService = new WorkspaceService();
    const response = await workspaceService.deleteWorkspace(workspace_id);

    if (response && !response.error) {
      // Copy the relevant workspaces array in the store
      const workspaces = org_workspace ? [] : [...state.userWorkspaces];
      // Find the index of the deleted workspace
      const workspaceIndex = workspaces.findIndex(
        (w) => w.workspace_id === workspace_id
      );
      // Remove the workspace from the array
      workspaces.splice(workspaceIndex, 1);

      // Update the relevant workspaces array in state
      if (org_workspace) {
      } else {
        commit('setUserWorkspaces', workspaces);
      }

      // Check if the deleted workspace was the active workspace
      if (
        state.activeWorkspace &&
        state.activeWorkspace.workspace_id === workspace_id
      ) {
        // Determine the workspace to load -- the workspace after the deleted workspace (now at the index of the deleted workspace after removing), the workspace before the deleted workspace, or null if neither exist
        const workspaceToLoad = workspaces[workspaceIndex]
          ? workspaces[workspaceIndex]
          : workspaces[workspaceIndex - 1]
          ? workspaces[workspaceIndex - 1]
          : null;
        // Update the active workspace in state to the workspace to load
        commit('setActiveWorkspace', workspaceToLoad);
      }

      commit('setError', null);
      return true;
    } else {
      commit('setError', 'Something went wrong while deleting the workspace');
      return false;
    }
  },
  async addTrackedEntityToWorkspaces({ commit, dispatch, state }, payload) {
    // Payload contains workspace_ids, entity id, entity_type, entity_display_name, source, alerts_on, alert_types, fetchTrackedEntitiesData (optional)
    // Build the request payload
    const request = {
      ...payload,
      user_id: sessionStorage.getItem('uid'),
    };
    // Make request to add tracked entity
    const workspaceService = new WorkspaceService();
    const response = await workspaceService.addTrackedEntity(request);

    if (response && !response.error) {
      // Update the workspace tracked entities in the store with the data returned from the service
      commit('addTrackedEntityToWorkspaces', response.data);
      // Check if we need to fetch tracked entities data for this entity type
      // Only fetch when new rows were actually inserted (response.data is non-empty)
      if (payload.fetchTrackedEntitiesData && state.activeWorkspace && response.data.length > 0) {
        // Get the unique entity ids for this entity type in the active workspace (now including the new entity)
        const entityIds = [
          ...new Set(
            state.activeWorkspace.tracked_entities[payload.entity_type].map(
              (entity) => entity.entity_id
            )
          ),
        ];
        // Fetch tracked entities data for this entity type
        dispatch('fetchTrackedEntitiesData', {
          entityType: payload.entity_type,
          entityIds,
          size: entityIds.length,
        });
      }
      commit('setError', null);
      return true;
    } else {
      commit(
        'setError',
        'Something went wrong while adding the entity to the workspace(s)'
      );
      return false;
    }
  },
  async removeTrackedEntityFromWorkspaces({ commit, state }, payload) {
    const { workspace_ids, entity_type, entity_id } = payload;
    const workspaceService = new WorkspaceService();
    const response = await workspaceService.deleteTrackedEntityFromWorkspaces(
      payload
    );

    if (response && !response.error) {
      // Find the workspaces in the store
      const workspaces = state.userWorkspaces.filter((w) =>
        workspace_ids.includes(w.workspace_id)
      );
      // Remove the tracked entity from the relevant array in each workspace
      workspaces.forEach((workspace) => {
        const filteredTrackedEntities = workspace.tracked_entities[
          entity_type
        ].filter((entity) => String(entity.entity_id) !== String(entity_id));
        // Update the workspace tracked entities in the store
        commit('updateWorkspaceTrackedEntities', {
          workspace,
          entity_type,
          data: filteredTrackedEntities,
        });
      });

      // Get the tracked entities data from state for this entity type
      const trackedEntityTypeData = state.trackedEntitiesData[entity_type];
      // Determine the id field for the entity type
      const idField =
        entity_type === 'assignee' || entity_type === 'inventor'
          ? 'psn_id'
          : 'id';
      // Filter the deleted tracked entities from the tracked entities data
      const updatedTrackedEntityTypeData = trackedEntityTypeData.filter(
        (entity) => entity[idField] != entity_id
      );
      // Build payload to update the tracked entities data in the store
      const trackedEntitiesDataPayload = {
        entityType: entity_type,
        data: updatedTrackedEntityTypeData,
      };
      // Commit the updated tracked entities data to the store
      commit('setTrackedEntitiesData', trackedEntitiesDataPayload);
      // Remove any alerts for this entity
      const filteredAlerts = state.alerts.filter((alert) => {
        return !(
          workspace_ids.includes(alert.workspace_id) &&
          alert.entity_type === entity_type &&
          entity_id == alert.entity_id
        );
      });
      // Commit the updated alerts to the store
      commit('setAlerts', filteredAlerts);
      commit('setError', null);
      return true;
    } else {
      commit(
        'setError',
        'Something went wrong while removing the entity from the workspace(s)'
      );
      return false;
    }
  },
  // Set active workspace to the workspace object that is the payload
  setActiveWorkspace({ commit }, payload) {
    // Commit mutation to set active workspace
    commit('setActiveWorkspace', payload);
  },
  // Query elasticsearch or postgres to get entities data to display in TrackedEntitiesList
  async fetchTrackedEntitiesData({ commit }, payload) {
    const { entityType, entityIds, size } = payload;
    // Set loading state
    commit('setTrackedEntitiesDataLoader', true);
    // Set source based on entityType
    let source = null;
    if (entityType === 'publication' || entityType === 'author') {
      source = 'publication';
    } else {
      source = 'patent';
    }
    // For publication and patent entity types, we need to query elasticsearch
    if (entityType === 'publication' || entityType === 'patent') {
      // Build filters -- Add entityIds in property matching the correct elasticsearch field name for that entity type
      const filters = {
        [fieldTypes[entityType]]: entityIds,
      };

      // Package up the request for the backend
      let request = {
        query: null,
        scan_type: 'boolean',
        filters,
        skip_aggs: true,
        source,
      };

      if (size) {
        // If size is passed in, add it to request along with flag for DocumentService
        request.size = size;
        request.isWorkspaceReq = true;
      }

      // Use document service to query elasticsearch
      const documentService = new DocumentService();
      let { documents, error } = await documentService.fetchDocuments(request);
      if (!error) {
        // Set loading state
        const _payload = {
          entityType,
          data: documents,
        };
        // Commit document data from elasticsearch to workspaces state
        commit('setTrackedEntitiesData', _payload);
        // Set loading state
        commit('setTrackedEntitiesDataLoader', false);
      }
    } else {
      // This is a person -- No need to query elasticsearch, query Postgres instead
      // Set loading state
      commit('setTrackedEntitiesDataLoader', true);
      // Build request for workspace service to query postgres for these entities' metadata
      const _request = {
        entity_type: entityType,
        entity_ids: entityIds,
      };
      const workspaceService = new WorkspaceService();
      let response = await workspaceService.fetchPersonMetadata(_request);
      if (response && !response.error) {
        const _payload = {
          entityType,
          data: response.data,
        };
        // Commit person data from tracked_entities to workspaces state
        commit('setTrackedEntitiesData', _payload);
      }
      // Always reset loading state regardless of response outcome
      commit('setTrackedEntitiesDataLoader', false);
    }
  },
  async deleteTrackedEntities({ commit, state }, payload) {
    const { workspace_id, entity_type, entity_ids } = payload;
    // Send payload to backend to delete tracked entities from a given workspace
    const workspaceService = new WorkspaceService();
    const response = await workspaceService.deleteTrackedEntitiesFromWorkspace(
      payload
    );
    if (response && !response.error) {
      // Tracked entities successfully deleted from workspace -- Remove Entities from the Workspace and Tracked Entities Data

      // Find the workspace in the store
      const workspace = state.userWorkspaces.find(
        (w) => w.workspace_id === workspace_id
      );
      // Filter the deleted tracked entities from the workspace tracked entities
      const filteredTrackedEntities = workspace.tracked_entities[
        entity_type
      ].filter((entity) => !entity_ids.some((id) => id == entity.entity_id));
      // Build payload to update the tracked entities in the store
      const workspaceTrackedEntitiesPayload = {
        workspace: workspace,
        entity_type: entity_type,
        data: filteredTrackedEntities,
      };
      // Commit the updated tracked entities to the store
      commit('updateWorkspaceTrackedEntities', workspaceTrackedEntitiesPayload);

      // Get the tracked entities data from state for this entity type
      const trackedEntityTypeData = state.trackedEntitiesData[entity_type];
      // Determine the id field for the entity type
      const idField =
        entity_type === 'assignee' || entity_type === 'inventor'
          ? 'psn_id'
          : 'id';
      // Filter the deleted tracked entities from the tracked entities data
      const updatedTrackedEntityTypeData = trackedEntityTypeData.filter(
        (entity) => !entity_ids.some((id) => id == entity[idField])
      );
      // Build payload to update the tracked entities data in the store
      const trackedEntitiesDataPayload = {
        entityType: entity_type,
        data: updatedTrackedEntityTypeData,
      };
      // Commit the updated tracked entities data to the store
      commit('setTrackedEntitiesData', trackedEntitiesDataPayload);
      // Filter out any alerts for these entities
      const filteredAlerts = state.alerts.filter((alert) => {
        return !(
          alert.workspace_id == workspace_id &&
          alert.entity_type === entity_type &&
          entity_ids.some((id) => id == alert.entity_id)
        );
      });
      // Commit the updated alerts to the store
      commit('setAlerts', filteredAlerts);
    } else {
      commit(
        'setError',
        'Something went wrong while deleting workspace entities'
      );
      return false;
    }
  },
  async fetchLinkedScansData({ commit }, payload) {
    if (!payload.skipLoader) commit('setLinkedScansDataLoader', true);
    const { workspaceId } = payload;
    const workspaceService = new WorkspaceService();
    const response = await workspaceService.getWorkspaceLinkedScans(
      workspaceId
    );

    if (response && !response.error) {
      const linkedScansData = response.data;
      commit('setLinkedScansData', linkedScansData);
      commit('setLinkedScansDataLoader', false);
      commit('setError', null);
      return true;
    } else {
      commit('setError', 'Something went wrong while getting linked scans');
      return false;
    }
  },
  async linkUnlinkScans({ commit, dispatch }, payload) {
    const { linkedScanIds, unlinkedScanIds, workspaceId } = payload;
    const _payload = {
      linked_scan_ids: linkedScanIds,
      unlinked_scan_ids: unlinkedScanIds,
      workspace_id: workspaceId,
    };
    const workspaceService = new WorkspaceService();
    const response = await workspaceService.linkUnlinkScans(_payload);

    // Check only for response -- there are multiple db transactions that happen on the backend, we still want to get updated data if one of them fails, e.g., linking may succeed but unlinking may fail
    if (response) {
      // Fetch linked scans data to update the UI. Await so we don't return a response to the component before the linked scans data is updated.
      await dispatch('fetchLinkedScansData', {
        workspaceId: payload.workspaceId,
        skipLoader: true,
      });
    }

    // Handle errors
    if (!response || response.error) {
      commit('setError', 'Something went wrong while updating linked scans');
      return false;
    } else {
      commit('setError', null);
      return true;
    }
  },
  async unlinkScanFromUserWorkspaces({ commit, rootGetters }, payload) {
    const { userId, scanId } = payload;
    const _payload = {
      user_id: userId,
      scan_id: scanId,
    };
    const workspaceService = new WorkspaceService();
    const response = await workspaceService.unlinkScanFromUserWorkspaces(
      _payload
    );
    if (response && !response.error) {
      const linkedScansData = rootGetters['workspaces/linkedScansData'];
      if (linkedScansData.length > 0) {
        const updatedLinkedScansData = linkedScansData.filter(
          (scan) => scan.scan_id !== scanId
        );
        commit('setLinkedScansData', updatedLinkedScansData);
      }
      return true;
    } else {
      commit(
        'setError',
        'Something went wrong while unlinking the scan from workspaces'
      );
      return false;
    }
  },
  async unlinkScanFromAllWorkspaces({ commit, rootGetters }, payload) {
    const { scanId } = payload;
    const _payload = {
      scan_id: scanId,
    };
    const workspaceService = new WorkspaceService();
    const response = await workspaceService.unlinkScanFromAllWorkspaces(
      _payload
    );
    if (response && !response.error) {
      const linkedScansData = rootGetters['workspaces/linkedScansData'];
      if (linkedScansData.length > 0) {
        const updatedLinkedScansData = linkedScansData.filter(
          (scan) => scan.scan_id !== scanId
        );
        commit('setLinkedScansData', updatedLinkedScansData);
      }
      return true;
    } else {
      commit(
        'setError',
        'Something went wrong while unlinking the scan from workspaces'
      );
      return false;
    }
  },
  async fetchAlerts({ commit, state }, payload) {
    // Clear any existing error
    commit('setError', null);
    // Set loading state
    !payload.skipLoader && commit('setAlertsDataLoader', true);
    // Get pagination from the store
    const pagination = state.alertsPagination;
    // Get the workspace ID from the store
    const workspaceId = state.activeWorkspace
      ? state.activeWorkspace.workspace_id
      : null;
    // Get the unreadAlertsOnly value from the store
    const unreadAlertsOnly = state.unreadAlertsOnly;

    if (workspaceId) {
      // Build request payload
      const request = {
        workspace_id: workspaceId,
        user_id: sessionStorage.getItem('uid'),
        unread_alerts_only: unreadAlertsOnly,
        pagination,
      };

      // Make request to workspace service to get alerts for the active workspace
      const workspaceService = new WorkspaceService();
      const response = await workspaceService.fetchAlerts(request);

      if (response && !response.error) {
        const { alerts, pagination } = response.data;
        // Commit mutation to set alerts in the store
        commit('setAlerts', alerts);
        // Set rowsNumber pagination data using Vue.set to trigger reactivity
        Vue.set(state.alertsPagination, 'rowsNumber', pagination.rowsNumber);
        commit('setAlertsDataLoader', false);
        commit('setError', null);
        return true;
      } else {
        commit('setError', 'Something went wrong while getting alerts');
        return false;
      }
    } else {
      commit('setAlerts', []);
      commit('setAlertsDataLoader', false);
      commit('setError', null);
      return true;
    }
  },
  async paginateAlerts({ commit, dispatch, state }, payload) {
    const { pagination, skipLoader } = payload;
    const paginationPayload = {
      ...state.alertsPagination,
      ...pagination,
    };
    // Update the pagination object in the store
    commit('setAlertsPagination', paginationPayload);

    // Fetch alerts for the active workspace -- await and return so component knows when alerts are fetched for local loading state
    return await dispatch('fetchAlerts', {
      skipLoader,
    });
  },
  async setUnreadAlertsOnly({ commit, dispatch, state }, payload) {
    // Update the unreadAlertsOnly property in the store
    commit('setUnreadAlertsOnly', payload);
    // Reset pagination and fetch alerts
    return await dispatch('paginateAlerts', {
      pagination: {
        page: 1,
        rowsPerPage: 10,
      },
      skipLoader: true,
    });
  },
  async markAlertsAsRead({ commit, state }, payload) {
    // Clear any existing error
    commit('setError', null);
    const { alerts } = payload;
    // Get the IDs of the alerts to be marked as read for each source
    const publicationAlertIds = alerts
      .filter((alert) => alert.source === 'publication')
      .map((alert) => alert.ingest_alerts_publications_id);
    const patentAlertIds = alerts
      .filter((alert) => alert.source === 'patent')
      .map((alert) => alert.ingest_alerts_patents_id);

    // Build request payload
    const request = {
      publicationAlertIds,
      patentAlertIds,
    };
    // Make request to workspace service to mark alerts as read
    const workspaceService = new WorkspaceService();
    const response = await workspaceService.markAlertsRead(request);
    if (response && !response.error) {
      // Iterate over alerts to mark those that were updated as read
      const updatedAlerts = state.alerts.map((alert) => {
        const idField = alert.publications
          ? 'ingest_alerts_publications_id'
          : 'ingest_alerts_patents_id';
        const alertToBeMarkedRead = alerts.find(
          (a) => a[idField] === alert[idField]
        );
        if (alertToBeMarkedRead) {
          return {
            ...alert,
            is_read: true,
          };
        } else {
          return alert;
        }
      });
      // Update the alerts in the store
      commit('setAlerts', updatedAlerts);
      // Update alerts pagination unread rowsNumber
      const updatedPagination = {
        ...state.alertsPagination,
        rowsNumber: {
          ...state.alertsPagination.rowsNumber,
          unread: state.alertsPagination.rowsNumber.unread - alerts.length,
        },
      };
      commit('setAlertsPagination', updatedPagination);
      commit('setError', null);
      return true;
    } else {
      commit('setError', 'Something went wrong while marking alerts as read');
      return false;
    }
  },
  async markAllAlertsAsRead({ commit, state }, payload) {
    // Clear any existing error
    commit('setError', null);
    const { workspaceId } = payload;
    // Build request payload
    const request = {
      workspace_id: workspaceId,
    };
    // Make request to workspace service to mark all alerts as read
    const workspaceService = new WorkspaceService();
    const response = await workspaceService.markAllAlertsRead(request);
    if (response && !response.error) {
      // Iterate over alerts and mark all as read
      const updatedAlerts = state.alerts.map((alert) => {
        return {
          ...alert,
          is_read: true,
        };
      });
      // Update the alerts in the store
      commit('setAlerts', updatedAlerts);
      // Update alerts pagination unread rowsNumber
      const updatedPagination = {
        ...state.alertsPagination,
        rowsNumber: {
          ...state.alertsPagination.rowsNumber,
          unread: 0,
        },
      };
      commit('setAlertsPagination', updatedPagination);
      commit('setError', null);
      return true;
    } else {
      commit('setError', 'Something went wrong while marking alerts as read');
      return false;
    }
  },
  async toggleAlertsOnOff({ commit, dispatch }, payload) {
    // Clear any existing error
    commit('setError', null);
    const { workspaceId, entityId, entityType, alertTypes, alertsOn } = payload;
    // Build request payload
    const request = {
      workspace_id: workspaceId,
      entity_id: entityId,
      entity_type: entityType,
      alert_types: alertTypes,
      alerts_on: alertsOn,
    };
    // Make request to workspace service to toggle alerts on/off for the tracked entity
    const workspaceService = new WorkspaceService();
    const response = await workspaceService.toggleAlertsOnOff(request);
    if (response && !response.error) {
      // Update the tracked entities in the store
      const workspace = state.userWorkspaces.find(
        (ws) => ws.workspace_id === workspaceId
      );
      const updatedTrackedEntities = workspace.tracked_entities[entityType].map(
        (entity) => {
          if (
            String(entity.entity_id) === String(entityId) &&
            alertTypes.includes(entity.alert_type)
          ) {
            return {
              ...entity,
              alerts_on: alertsOn,
            };
          } else {
            return entity;
          }
        }
      );
      commit('updateWorkspaceTrackedEntities', {
        workspace,
        entity_type: entityType,
        data: updatedTrackedEntities,
      });
      // Fetch alerts for the active workspace -- await and return so component knows when alerts are fetched for local loading state
      return await dispatch('fetchAlerts', {
        skipLoader: false,
      });
    } else {
      commit('setError', 'Something went wrong while toggling alerts');
      return false;
    }
  },
  clearStore({ commit }) {
    commit('setUserWorkspaces', []);
    commit('setUserWorkspacesLoading', true);
    commit('setActiveWorkspace', null);
    commit('setError', null);
    commit('setAlerts', []);
  },
};

const mutations = {
  setUserWorkspaces(state, payload) {
    state.userWorkspaces = payload;
  },
  setUserWorkspacesLoading(state, payload) {
    state.userWorkspacesLoading = payload;
  },
  setActiveWorkspace(state, payload) {
    state.activeWorkspace = payload;
  },
  addTrackedEntityToWorkspaces(state, payload) {
    // Iterate over the data returned from the service
    for (const trackedEntity of payload) {
      // Find the workspace in the store
      const workspace = state.userWorkspaces.find(
        (workspace) => workspace.workspace_id === trackedEntity.workspace_id
      );
      // Add the tracked entity to the relevant array in the workspace
      Vue.set(
        workspace.tracked_entities,
        trackedEntity.entity_type,
        workspace.tracked_entities[trackedEntity.entity_type].concat(
          trackedEntity
        )
      );
    }
  },
  updateWorkspaceTrackedEntities(state, payload) {
    const { workspace, entity_type, data } = payload;
    // Update the active workspace if it matches the workspace in the payload
    if (
      state.activeWorkspace &&
      state.activeWorkspace.workspace_id === workspace.workspace_id
    ) {
      Vue.set(state.activeWorkspace.tracked_entities, entity_type, data);
    }
    // Update the workspace in the store workspaces array
    Vue.set(workspace.tracked_entities, entity_type, data);
  },
  setError(state, payload) {
    state.error = payload;
  },
  setTrackedEntitiesDataLoader(state, payload) {
    state.trackedEntitiesDataLoader = payload;
  },
  setTrackedEntitiesData(state, payload) {
    // Use Vue.set to force reactivity for nested objects and their watchers
    Vue.set(state.trackedEntitiesData, payload.entityType, payload.data);
  },
  setLinkedScansData(state, payload) {
    Vue.set(state, 'linkedScansData', payload);
  },
  setLinkedScansDataLoader(state, payload) {
    state.linkedScansDataLoader = payload;
  },
  setAlerts(state, payload) {
    Vue.set(state, 'alerts', payload);
  },
  setAlertsDataLoader(state, payload) {
    state.alertsDataLoader = payload;
  },
  setAlertsPagination(state, payload) {
    state.alertsPagination = payload;
  },
  setUnreadAlertsOnly(state, payload) {
    state.unreadAlertsOnly = payload;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
